/**
 * Reduces a configured entry to the hostname pattern it means.
 *
 * Entries arrive in two shapes: bare domains and wildcards from the remote
 * config, and full origins from the hardcoded microsite configs. The scheme and
 * any trailing slash carry nothing the match needs — every entry is treated as
 * an HTTPS origin regardless of how it was written.
 */
function toHostnamePattern(entry: string): string {
  return entry
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
}

/**
 * Answers whether the page embedding us is one the given whitelist names.
 *
 * A bare entry names one host and nothing beneath it; subdomains need an
 * explicit `*.`, which in turn excludes the apex. The two forms are disjoint.
 * This mirrors `isTrustedOrigin` in frontend-core, which the buyflow enforces
 * immediately after us — a looser rule here just moves the refusal to a later
 * step, where the visitor has already committed to a checkout.
 *
 * Inbound only: this asks who is allowed to put us in a frame, which is the
 * opposite direction from the other readers of the same config, where the list
 * names hosts we send postMessages to.
 */
export function isEmbeddingHostAllowed(
  origin: string,
  whitelist: string[]
): boolean {
  let parsed: URL;

  try {
    parsed = new URL(origin);
  } catch {
    // An origin we cannot read is not an origin we can vouch for.
    return false;
  }

  const { protocol, hostname } = parsed;

  // Entries are HTTPS origins, so an insecure origin is not a host we trusted,
  // however well its hostname lines up. This is checked ahead of the entries,
  // so a bare `*` does not licence http either — the callers that rely on `*`
  // run outside production, where this matcher is not consulted.
  if (protocol !== 'https:') {
    return false;
  }

  return whitelist.map(toHostnamePattern).some((pattern) => {
    if (pattern === '*') {
      return true;
    }

    if (pattern.startsWith('*.')) {
      // The leading dot stays in the suffix on purpose: it forces a label
      // boundary, so `*.hinthome.com` covers `app.hinthome.com` but neither the
      // bare apex nor a lookalike like `nothinthome.com`.
      return hostname.endsWith(pattern.slice(1));
    }

    return pattern === hostname;
  });
}
